import { apiFetch } from "@/lib/apiClient";
import type {
  AccessRequest,
  CaregiverAccess,
  CaregiverCategoryValue,
  Dependent,
  IncomingPatientAccessRequest,
  InvitePreview,
} from "@/shared/types/family";

interface ApiResult<T> {
  data: T | null;
  error: string | null;
  code: string | null;
  /** Present only on TOO_MANY_ATTEMPTS-style rate-limit errors (see resendCaregiverInviteApi). */
  retryAfterSeconds?: number;
}

function toResult<T>(
  data: T | null,
  error?: { message: string; code: string; retryAfterSeconds?: number } | null,
): ApiResult<T> {
  if (error) return { data: null, error: error.message, code: error.code, retryAfterSeconds: error.retryAfterSeconds };
  return { data, error: null, code: null };
}

export interface CreateDependentPayload {
  firstName: string;
  lastName: string;
  email?: string;
  dob: string;
  relationship: string;
}

export interface InviteCaregiverPayload {
  email: string;
  categories: CaregiverCategoryValue[];
  /** ISO datetime string, or omitted entirely for "no expiration". */
  expiresAt?: string;
}

export interface ManageCaregiverAccessPayload {
  categories: CaregiverCategoryValue[];
  /** ISO datetime string, or omitted entirely for "no expiration". */
  expiresAt?: string;
}

interface DependentListResponse {
  data: Dependent[];
}

interface CaregiverListResponse {
  data: CaregiverAccess[];
}

export async function listDependentsApi() {
  const { data, error } = await apiFetch<DependentListResponse>("/api/patient/family/dependents", {
    method: "GET",
    portal: "patient",
  });
  return toResult(data?.data ?? null, error);
}

export async function createDependentApi(payload: CreateDependentPayload) {
  const { data, error } = await apiFetch<Dependent>("/api/patient/family/dependents", {
    method: "POST",
    body: JSON.stringify(payload),
    portal: "patient",
  });
  return toResult(data, error);
}

export async function updateDependentApi(dependentId: string, payload: CreateDependentPayload) {
  const { data, error } = await apiFetch<Dependent>(`/api/patient/family/dependents/${dependentId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    portal: "patient",
  });
  return toResult(data, error);
}

export async function listCaregiversApi() {
  const { data, error } = await apiFetch<CaregiverListResponse>("/api/patient/family/caregivers", {
    method: "GET",
    portal: "patient",
  });
  return toResult(data?.data ?? null, error);
}

export async function inviteCaregiverApi(payload: InviteCaregiverPayload) {
  const { data, error } = await apiFetch<CaregiverAccess>("/api/patient/family/caregivers/invite", {
    method: "POST",
    body: JSON.stringify(payload),
    portal: "patient",
  });
  return toResult(data, error);
}

/** Re-sends a still-pending or already-expired invite (fresh token, renewed expiry) — rejected once accepted. */
export async function resendCaregiverInviteApi(relationshipId: string) {
  const { data, error } = await apiFetch<CaregiverAccess>(`/api/patient/family/caregivers/${relationshipId}/resend`, {
    method: "POST",
    portal: "patient",
  });
  return toResult(data, error);
}

/** Cancels a still-pending invite — rejected once accepted or already expired. */
export async function cancelCaregiverInviteApi(relationshipId: string) {
  const { data, error } = await apiFetch<{ message?: string }>(`/api/patient/family/caregivers/${relationshipId}/cancel`, {
    method: "POST",
    portal: "patient",
  });
  return toResult(data, error);
}

/** Replaces the shared categories and access duration for an already-active caregiver — rejected otherwise. */
export async function updateCaregiverAccessApi(relationshipId: string, payload: ManageCaregiverAccessPayload) {
  const { data, error } = await apiFetch<CaregiverAccess>(`/api/patient/family/caregivers/${relationshipId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    portal: "patient",
  });
  return toResult(data, error);
}

/** Immediately revokes an already-active caregiver's access — rejected otherwise. */
export async function revokeCaregiverAccessApi(relationshipId: string) {
  const { data, error } = await apiFetch<{ message?: string }>(`/api/patient/family/caregivers/${relationshipId}/revoke`, {
    method: "POST",
    portal: "patient",
  });
  return toResult(data, error);
}

/**
 * Resolves an invite email link's token before any account exists —
 * deliberately unauthenticated (no `portal`, `skipAuth: true`), same as
 * signup/signin themselves.
 */
export async function getInvitePreviewApi(token: string) {
  const { data, error } = await apiFetch<InvitePreview>(`/api/patient/family/invite-preview?token=${encodeURIComponent(token)}`, {
    method: "GET",
    skipAuth: true,
  });
  return toResult(data, error);
}

/** Backs the decline-without-login confirmation page — resolves either template's token. */
export async function getDeclinePreviewApi(token: string) {
  const { data, error } = await apiFetch<InvitePreview>(`/api/patient/family/invite-decline-preview?token=${encodeURIComponent(token)}`, {
    method: "GET",
    skipAuth: true,
  });
  return toResult(data, error);
}

/** Declines an invite by its raw email-link token — no sign-up or login required. */
export async function declineInviteByTokenApi(token: string) {
  const { data, error } = await apiFetch<{ message?: string }>(`/api/patient/family/invite-decline?token=${encodeURIComponent(token)}`, {
    method: "POST",
    skipAuth: true,
  });
  return toResult(data, error);
}

interface AccessRequestListResponse {
  data: AccessRequest[];
}

/** Incoming invites addressed to the current user as a viewer — not yet accepted or declined. */
export async function listAccessRequestsApi() {
  const { data, error } = await apiFetch<AccessRequestListResponse>("/api/patient/family/access-requests", {
    method: "GET",
    portal: "patient",
  });
  return toResult(data?.data ?? null, error);
}

export async function acceptAccessRequestApi(relationshipId: string) {
  const { data, error } = await apiFetch<{ message?: string }>(`/api/patient/family/access-requests/${relationshipId}/accept`, {
    method: "POST",
    portal: "patient",
  });
  return toResult(data, error);
}

export async function declineAccessRequestApi(relationshipId: string) {
  const { data, error } = await apiFetch<{ message?: string }>(`/api/patient/family/access-requests/${relationshipId}/decline`, {
    method: "POST",
    portal: "patient",
  });
  return toResult(data, error);
}

interface IncomingPatientAccessRequestListResponse {
  data: IncomingPatientAccessRequest[];
}

/** Direction A — caregiver-initiated requests waiting on this patient's own review (accept/decline). */
export async function listIncomingPatientRequestsApi() {
  const { data, error } = await apiFetch<IncomingPatientAccessRequestListResponse>("/api/patient/family/patient-access-requests", {
    method: "GET",
    portal: "patient",
  });
  return toResult(data?.data ?? null, error);
}

export async function acceptIncomingPatientRequestApi(relationshipId: string) {
  const { data, error } = await apiFetch<{ message?: string }>(`/api/patient/family/patient-access-requests/${relationshipId}/accept`, {
    method: "POST",
    portal: "patient",
  });
  return toResult(data, error);
}

export async function declineIncomingPatientRequestApi(relationshipId: string) {
  const { data, error } = await apiFetch<{ message?: string }>(`/api/patient/family/patient-access-requests/${relationshipId}/decline`, {
    method: "POST",
    portal: "patient",
  });
  return toResult(data, error);
}

/** A caregiver's "request more categories" ask on an already-active grant — see RequestMoreAccessModal. */
export async function approveCategoryRequestApi(requestId: string) {
  const { data, error } = await apiFetch<{ message?: string }>(`/api/patient/family/category-requests/${requestId}/approve`, {
    method: "POST",
    portal: "patient",
  });
  return toResult(data, error);
}

export async function declineCategoryRequestApi(requestId: string) {
  const { data, error } = await apiFetch<{ message?: string }>(`/api/patient/family/category-requests/${requestId}/decline`, {
    method: "POST",
    portal: "patient",
  });
  return toResult(data, error);
}
